import React, { useState, useEffect, useRef } from 'react';
import { Label, Tag, Text, Transformer, Group } from 'react-konva';
import PropTypes from 'prop-types';

const LABEL_TYPES = {
  TOOLTIP_DOWN: {
    label: 'Tooltip Down',
    tagProps: {
      fill: 'grey',
      pointerDirection: 'down',
      pointerWidth: 10,
      pointerHeight: 10,
      lineJoin: 'round',
      shadowColor: 'black',
      shadowBlur: 10,
      shadowOffsetX: 5,
      shadowOffsetY: 5,
      shadowOpacity: 0.5
    },
    textProps: {
      fontFamily: 'Calibri',
      fontSize: 18,
      padding: 5,
      fill: 'white'
    }
  },
  LABEL_LEFT: {
    label: 'Label Left',
    tagProps: {
      fill: 'green',
      pointerDirection: 'left',
      pointerWidth: 20,
      pointerHeight: 28,
      lineJoin: 'round'
    },
    textProps: {
      fontFamily: 'Calibri',
      fontSize: 18,
      padding: 5,
      fill: 'white'
    }
  },
  SIMPLE: {
    label: 'Simple Label',
    tagProps: {
      fill: 'yellow'
    },
    textProps: {
      fontFamily: 'Calibri',
      fontSize: 18,
      padding: 5,
      fill: 'black'
    }
  }
};

const DraggableLabel = ({ 
  shape,
  isSelected,
  onSelect,
  onErase,
  stageRef,
  tool,
  setShapes,
  pushHistory
}) => {
  const shapeRef = useRef();
  const transformerRef = useRef();
  const textRef = useRef();
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(shape.text || '');

  // Update editedText when shape.text changes
  useEffect(() => {
    setEditedText(shape.text || '');
  }, [shape.text]);

  // Handle transformer when selection changes
  useEffect(() => {
    if (isSelected && transformerRef.current) {
      const node = stageRef.current?.findOne(`#${shape.id}`);
      if (node) {
        transformerRef.current.nodes([node]);
      } else {
        transformerRef.current.nodes([]);
      }
    }
  }, [isSelected, shape.id, stageRef]);

  const handleTextChange = (newText) => {
    setShapes(prev =>
      prev.map(s =>
        s.id === shape.id ? { ...s, text: newText } : s
      )
    );
    pushHistory();
  };

  const handleDragEnd = (e) => {
    const node = e.target;
    setShapes(prev => {
      const updatedShapes = prev.map(s =>
        s.id === shape.id
          ? { ...s, x: node.x(), y: node.y() }
          : s
      );
      pushHistory(updatedShapes);
      return updatedShapes;
    });
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation ? node.rotation() : 0;

    setShapes(prev => {
      const updatedShapes = prev.map(s => {
        if (s.id !== shape.id) return s;
        
        return {
          ...s,
          x: node.x(),
          y: node.y(),
          width: Math.max(10, s.width * scaleX),
          height: Math.max(10, s.height * scaleY),
          rotation: rotation
        };
      });
      pushHistory(updatedShapes);
      return updatedShapes;
    });
  };

  const handleClick = (e) => {
    if (tool === 'eraser') {
      onErase();
    } else {
      onSelect();
    }
  };

  const labelType = LABEL_TYPES[shape.labelType];
  const minWidth = shape.minWidth || 100;

  return (
    <>
      <Label
        x={shape.x}
        y={shape.y}
        id={shape.id}
        ref={shapeRef}
        draggable={tool === 'select'}
        onClick={handleClick}
        onTap={handleClick}
        onDblClick={() => setIsEditing(true)}
        onDblTap={() => setIsEditing(true)}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
        opacity={0.75}
      >
        <Tag {...labelType.tagProps} />
        <Text
          ref={textRef}
          text={shape.text}
          {...labelType.textProps}
          width={minWidth}
          onDblClick={(e) => {
            e.cancelBubble = true;
            setIsEditing(true);
          }}
          onDblTap={(e) => {
            e.cancelBubble = true;
            setIsEditing(true);
          }}
        />
        {isEditing && (
          <Group>
            <Text
              text={editedText}
              x={0}
              y={0}
              width={Math.max(minWidth, 150)}
              padding={4}
              fill="black"
              align="left"
              fontFamily="Arial"
              fontSize={14}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTextChange(editedText.trim());
                  setIsEditing(false);
                } else if (e.key === 'Escape') {
                  setIsEditing(false);
                }
              }}
              onBlur={() => {
                handleTextChange(editedText.trim());
                setIsEditing(false);
              }}
              onTextChange={(e) => setEditedText(e.currentTarget.text())}
              editable
              perfectDrawEnabled={false}
              _useStrictMode
            />
          </Group>
        )}
      </Label>
      {tool === 'select' && isSelected && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={true}
          anchorSize={8}
          boundBoxFunc={(oldBox, newBox) => {
            // Ensure minimum width
            if (newBox.width < 100) {
              newBox.width = 100;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

DraggableLabel.propTypes = {
  shape: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    labelType: PropTypes.string.isRequired,
    minWidth: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onErase: PropTypes.func.isRequired,
  stageRef: PropTypes.object.isRequired,
  tool: PropTypes.string.isRequired,
  setShapes: PropTypes.func.isRequired,
  pushHistory: PropTypes.func.isRequired,
};

export default DraggableLabel;
